import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, Clock, Check, RefreshCw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * GenericShipmentRequest - Reusable Shipment Request component for all sections
 * 
 * Shows transfers FROM this section with status "requested" - Step 3 & 4 of workflow
 * Allows source section to confirm shipment
 * 
 * Props:
 * - sectionName: Display name (e.g., "Inhouse Section")
 * - sectionKey: Database key (e.g., "Inhouse")
 * - backRoute: Route to go back to
 */

const GenericShipmentRequest = ({ sectionName, sectionKey, backRoute }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pendingShipments, setPendingShipments] = useState([]);
  const [confirmingShipment, setConfirmingShipment] = useState(null);

  useEffect(() => {
    fetchShipmentRequests();
  }, []);

  const fetchShipmentRequests = async () => {
    setLoading(true);
    try {
      // Get transfers FROM this section with status "requested"
      const response = await apiClient.get('/internal-transfer/shipment-requests', {
        params: { from_section: sectionKey }
      });
      setPendingShipments(response.data.transfers || []);
    } catch (error) {
      console.error('Error fetching shipment requests:', error);
      toast.error('Failed to load shipment requests');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmShipment = async (transferId) => {
    setConfirmingShipment(transferId);
    try {
      await apiClient.post('/internal-transfer/confirm-shipment', {
        transfer_id: transferId
      });
      toast.success('Shipment confirmed successfully');
      fetchShipmentRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setConfirmingShipment(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(backRoute)} 
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Shipment Requests</h1>
              <p className="text-sm text-[#E8DCC8]">{sectionName}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchShipmentRequests}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {pendingShipments.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-gray-400">
                <Send className="w-16 h-16 mx-auto mb-3 opacity-20" />
                <p className="text-lg font-medium">No pending shipment requests</p>
                <p className="text-sm mt-1">
                  When other sections request consignments, they will appear here for you to confirm shipment
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <Clock className="w-5 h-5" />
                Pending Shipment Requests ({pendingShipments.length})
              </CardTitle>
              <CardDescription>
                Other sections have requested these transfers - confirm when goods are shipped
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pendingShipments.map((shipment) => (
                  <div 
                    key={shipment.transfer_id} 
                    className="p-4 bg-orange-50 rounded-lg border border-orange-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                            {shipment.transfer_id}
                          </Badge>
                          <span className="text-sm text-gray-600">→</span>
                          <Badge className="bg-purple-100 text-purple-700 border-0">
                            {shipment.to_section}
                          </Badge>
                          <Badge className="bg-yellow-100 text-yellow-700 border-0">
                            <Clock className="w-3 h-3 mr-1" />
                            Requested
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700">
                          <strong>{shipment.bundle_count || shipment.bundle_codes?.length || 0}</strong> bundles
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Requested by {shipment.requested_by_name || 'Unknown'} on {shipment.requested_at ? new Date(shipment.requested_at).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      <Button
                        onClick={() => handleConfirmShipment(shipment.transfer_id)}
                        disabled={confirmingShipment === shipment.transfer_id}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`confirm-shipment-${shipment.transfer_id}`}
                      >
                        {confirmingShipment === shipment.transfer_id ? (
                          <>Confirming...</>
                        ) : (
                          <>
                            <Truck className="w-4 h-4 mr-2" />
                            Confirm Shipment
                          </>
                        )}
                      </Button>
                    </div>
                    
                    {/* Bundle list preview */}
                    {shipment.bundle_codes && shipment.bundle_codes.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <p className="text-xs text-gray-500 mb-2">Bundle Codes:</p>
                        <div className="flex flex-wrap gap-1">
                          {shipment.bundle_codes.slice(0, 8).map((code) => (
                            <Badge key={code} variant="outline" className="text-xs bg-white">
                              {code}
                            </Badge>
                          ))}
                          {shipment.bundle_codes.length > 8 && (
                            <Badge variant="outline" className="text-xs bg-gray-100">
                              +{shipment.bundle_codes.length - 8} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default GenericShipmentRequest;
